import {
  useState,
  useEffect,
  useRef,
  useMemo,
  type ReactNode,
} from 'react';
import { useEventSelection } from '../../contexts/useEventSelection';
import stationObservationsService, {
  type StationData,
} from '../../api/stationObservationsService';
import { getEventSource } from '../../utils/eventSource';
import { usePlayerOptional } from './usePlayerContext';
import { useDashboard } from './useDashboard';
import {
  StationObservationsCtx,
  type StationObservationsContextValue,
} from './stationObservationsCtx';
import { dashboardNeedsStationObservations } from './stationObservationsUtils';

export const StationObservationsProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const { draftWidgets } = useDashboard();
  const { selectedEvent } = useEventSelection();
  const player = usePlayerOptional();
  const selectedDateRange = player?.selectedDateRange ?? null;

  const needsObservations = useMemo(
    () => dashboardNeedsStationObservations(draftWidgets),
    [draftWidgets],
  );

  const [stations, setStations] = useState<StationData[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const isFirstLoadRef = useRef(true);

  useEffect(() => {
    isFirstLoadRef.current = true;
    setStations([]);
    setLoading(false);
    setError(null);
  }, [selectedEvent]);

  useEffect(() => {
    if (!needsObservations || !selectedEvent || !selectedDateRange) {
      setStations([]);
      setLoading(false);
      setError(null);
      return;
    }

    let cancelled = false;
    const isFirstLoad = isFirstLoadRef.current;

    if (isFirstLoad) {
      setLoading(true);
      setError(null);
    }

    const fetchData = async () => {
      try {
        const source = getEventSource(selectedEvent);
        const res = await stationObservationsService.getStationObservations(
          selectedEvent.season_year,
          selectedEvent.circuit_id,
          selectedEvent.start_date,
          source,
          selectedDateRange.start.toISOString(),
          selectedDateRange.end.toISOString(),
        );
        if (!cancelled) {
          setStations(res.stations || []);
          setError(null);
          if (isFirstLoad) {
            isFirstLoadRef.current = false;
            setLoading(false);
          }
        }
      } catch (err) {
        if (!cancelled) {
          if (isFirstLoad) {
            setError(
              err instanceof Error
                ? err.message
                : 'Failed to load observations',
            );
            setLoading(false);
          } else {
            console.warn('Station observations silent refresh failed:', err);
          }
        }
      }
    };

    fetchData();
    return () => {
      cancelled = true;
    };
  }, [needsObservations, selectedEvent, selectedDateRange]);

  const value = useMemo<StationObservationsContextValue>(
    () => ({
      stations,
      loading,
      error,
      needsObservations,
    }),
    [stations, loading, error, needsObservations],
  );

  return (
    <StationObservationsCtx.Provider value={value}>
      {children}
    </StationObservationsCtx.Provider>
  );
};
